import { useEffect, useMemo } from "react";

import {
  OverviewAvgActiveChart,
  OverviewSwitchInChart
} from "../../components/charts3";

import "./ChannelOverview.css";

import { useImpressionsData } from "../../hooks/useImpressionsData";
import { useLiveData } from "../../hooks/useLiveData";
import { useDaysSorted } from "../../hooks/useDaysSorted";
import { useYesterdayMetrics } from "../../hooks/useYesterdayMetrics";
import { useOverviewKpis } from "../../hooks/useOverviewKpis";
import { formatNumber } from "../../utils/formatNumber";

import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";

export default function ChannelOverview({ channelId, refreshKey, channelInfo, rawVideo }) {

  const storedUser = JSON.parse(
    localStorage.getItem("adm_panel_user") || "{}"
  );

  const TTVOverview = !!storedUser.ttvoverview;

  const { logPageChange } = useUserJourneyLogger({
    channelId,
    channelName: channelInfo?.name || channelInfo?.n || "unknown",
  });

  useEffect(() => {
    logPageChange({
      chartType: "overview",
      mode: "default",
    });
  }, [logPageChange]);


  const { liveValue } = useLiveData(channelId);
  const { rawImpressions } = useImpressionsData(channelId, refreshKey);

  const videoKeys = useDaysSorted(rawVideo);
  const impKeys = useDaysSorted(rawImpressions);

  const yesterdayKey = videoKeys?.at(-2) || null;
  const yesterdayImpKey = impKeys?.at(-2) || null;

  const weekBeforeKey = videoKeys?.at(-9) || null;
  const weekBeforeImpKey = impKeys?.at(-9) || null;

  const { avgActiveYesterday, switchInYesterday } =
    useYesterdayMetrics(rawVideo, rawImpressions, yesterdayKey, yesterdayImpKey);

  const { avgActiveYesterday: avgActiveWeekBefore, switchInYesterday: switchInWeekBefore } =
    useYesterdayMetrics(rawVideo, rawImpressions, weekBeforeKey, weekBeforeImpKey);

  const {
    videoConsumedYesterday,
    switchInYesterday: switchInYesterdayKpi,
    loading: kpiLoading,
  } = useOverviewKpis(channelId);

  /* -----------------------------
     TOTAL VIEWERS TR (NEW)
  ----------------------------- */

  function generateTotalFactorByMinute(minuteIndex) {
    const phaseAngle = (2 * Math.PI * minuteIndex) / 1440;

    let factor =
      (3.14 + Math.cos(phaseAngle + Math.PI / 2) * 0.5) *
      5.5 *
      0.95;

    const noise = Math.random() * 0.04 - 0.02;
    return factor + noise;
  }

  const totalViewersYesterday = useMemo(() => {
    if (!switchInYesterdayKpi) return 0;

    const avgFactor = 3.14 * 5.5 * 0.95;

    return Math.round(switchInYesterdayKpi * avgFactor);
  }, [switchInYesterdayKpi]);

  const totalViewersSeries = useMemo(() => {
    if (!switchInYesterday?.length) return [];

    return switchInYesterday.map((p, i) => {
      const minuteIndex = i * 60;
      const factor = generateTotalFactorByMinute(minuteIndex);

      return {
        ...p,
        value: Math.round((p.value || 0) * factor),
      };
    });
  }, [switchInYesterday]);

  /* -----------------------------
     VIEWER CALCULATION (ESKİ - DURUYOR)
  ----------------------------- */

  const viewerFactors = useMemo(() => {
    const arr = [];
    for (let m = 0; m < 1440; m++) {
      const phase = (2 * Math.PI * m) / 1440;
      const value = 3.14 + Math.cos(phase + Math.PI / 2) * 0.5;
      arr.push(value);
    }
    return arr;
  }, []);

  const getCurrentMinuteIndex = () => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  };

  const activeViewerNumber = useMemo(() => {
    if (!liveValue || !TTVOverview) return null;

    const idx = getCurrentMinuteIndex();
    const factor = viewerFactors[idx] || 1;

    return Math.round(liveValue * factor);
  }, [liveValue, viewerFactors, TTVOverview]);

  /* -----------------------------
     RENDER
  ----------------------------- */

  return (
    <>
      <div className="overview-cards">

        {/* ACTIVE TV */}
        <div className="overview-card card-red">
          <div className="card-icon">🕒</div>

          <div className="card-number">
            {TTVOverview
              ? `${formatNumber(liveValue)} / ${formatNumber(activeViewerNumber)}`
              : formatNumber(liveValue)}
          </div>

          <div className="card-title">
            {TTVOverview
              ? "active TV / active viewers"
              : "active TV"}
          </div>
        </div>

        {/* VIDEO CONSUMED */}
        <div className="overview-card card-green">
          <div className="card-icon">▶</div>
          <div className="card-number">
            {kpiLoading ? "…" : formatNumber(videoConsumedYesterday)} hrs
          </div>
          <div className="card-title">video consumed yesterday</div>
        </div>

        {/* SWITCH IN */}
        <div className="overview-card card-orange">
          <div className="card-icon">👁</div>
          <div className="card-number">
            {kpiLoading ? "…" : formatNumber(switchInYesterdayKpi)}
          </div>
          <div className="card-title">switch-in yesterday</div>
        </div>

        {/* ✅ TOTAL VIEWERS TR (REPLACED) */}
        <div className="overview-card card-blue">
          <div className="card-icon">🖥</div>
          <div className="card-number">
            {kpiLoading ? "…" : formatNumber(totalViewersYesterday)}
          </div>
          <div className="card-title">total viewers TR yesterday</div>
        </div>
      </div>

      <div className="overview-charts">
        <div className="chart-box">
          <OverviewAvgActiveChart
            series={avgActiveYesterday}
            seriesWeekBefore={avgActiveWeekBefore}
            mode="hour"
            chartType="bar"
          />
        </div>

        <div className="chart-box">
          {switchInYesterday?.length > 0 && (
            <OverviewSwitchInChart
              series={switchInYesterday}
              seriesWeekBefore={switchInWeekBefore}
              mode="hour"
              chartType="bar"
            />
          )}
        </div>

        {/* ✅ TOTAL VIEWERS CHART */}
        <div className="chart-box">
          {totalViewersSeries.length ? (
            <OverviewSwitchInChart
              title="Total Viewers Türkiye"
              series={totalViewersSeries}
              showComparison={false}
              mode="hour"
              chartType="bar"
            />
          ) : (
            <div style={{ padding: 40, opacity: 0.4 }}>(no data)</div>
          )}
        </div>
      </div>
    </>
  );
}